/**
 * CRA3D AI Training — Month-level knowledge check (Round-16).
 *
 * Renders a shuffled 6-question MCQ pulled from the matching month's
 * six lesson questions. The server never ships the correct option in
 * the question payload; the client submits each answer by lesson_id
 * and the server returns per-question correctness + feedback. Below
 * the pass threshold NO progress is recorded, so retries are
 * free — this honours Mark's "unlimited retries" instruction without
 * polluting the training_progress collection.
 *
 * Scope: Month 4 is the first section to ship this view; the route is
 * generic (`/training/month-knowledge-check/:section`) so future
 * month knowledge checks reuse this page unchanged.
 *
 * Honesty rules enforced here:
 *   - No "certification" / "assessment" wording in the UI.
 *   - No AI call; no network traffic except the two documented
 *     endpoints.
 *   - No auto-completion on page open — the completion row is only
 *     written server-side when the submission scores >= pass_score.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, CheckCircle2, Circle, HelpCircle, RotateCcw,
} from "lucide-react";


export default function TrainingKnowledgeCheck() {
  const { section } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({
    questions: [], pass_score: 5, total: 6,
  });
  const [picks, setPicks] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeNeeded, setUpgradeNeeded] = useState(false);
  const [schedBlocked, setSchedBlocked] = useState(false);

  async function loadQuestions() {
    setLoading(true);
    setError(null);
    setUpgradeNeeded(false);
    setSchedBlocked(false);
    setResult(null);
    setPicks({});
    try {
      const r = await api.get(
        `/training/months/${section}/knowledge-check`,
      );
      setState({
        questions:  r.data?.questions || [],
        pass_score: r.data?.pass_score || 5,
        total:      r.data?.total || 6,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        setUpgradeNeeded(true);
        setError(extractApiError(
          err,
          "This knowledge check is included with the annual AI Training membership.",
        ));
      } else if (status === 423) {
        setSchedBlocked(true);
        setError(extractApiError(
          err,
          "This month hasn't unlocked on your program timeline yet.",
        ));
      } else {
        setError(extractApiError(
          err, "Couldn't load the knowledge check.",
        ));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadQuestions(); /* eslint-disable-next-line */ }, [section]);

  function pick(lessonId, label) {
    setPicks((p) => ({ ...p, [lessonId]: label }));
  }

  async function submit() {
    setSubmitting(true);
    try {
      const r = await api.post(
        `/training/months/${section}/knowledge-check`,
        { answers: picks },
      );
      setResult(r.data);
    } catch (err) {
      setError(extractApiError(err, "Couldn't submit answers."));
    } finally {
      setSubmitting(false);
    }
  }

  function retry() {
    loadQuestions();
  }

  const answered = state.questions.length > 0 &&
    state.questions.every((q) => !!picks[q.lesson_id]);

  return (
    <AppShell>
      <div
        data-testid="training-knowledge-check-page"
        className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav("/training")}
          data-testid="training-knowledge-check-back-btn"
          className="mb-4 text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Back to AI Training
        </Button>

        {loading && (
          <p
            data-testid="training-knowledge-check-loading"
            className="text-sm text-fg-secondary text-center py-12"
          >
            Loading knowledge check&hellip;
          </p>
        )}

        {!loading && (upgradeNeeded || schedBlocked) && (
          <Alert
            variant="destructive"
            data-testid="training-knowledge-check-blocked"
            className="mb-4"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !upgradeNeeded && !schedBlocked && error && (
          <Alert
            variant="destructive"
            data-testid="training-knowledge-check-error"
            className="mb-4"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && state.questions.length > 0 && (
          <article>
            <Badge
              data-testid="training-knowledge-check-label"
              className="bg-bg-surface-hover text-fg-secondary hover:bg-bg-surface-hover uppercase text-[10px] tracking-widest border border-border-subtle"
            >
              {section.replace("_", " ")} &middot; Knowledge check
            </Badge>
            <h1
              data-testid="training-knowledge-check-title"
              className="mt-2 text-2xl sm:text-3xl font-heading font-medium text-fg-primary leading-tight"
            >
              Month 4 Final knowledge check
            </h1>
            <p
              data-testid="training-knowledge-check-pass-rule"
              className="mt-2 text-sm text-fg-secondary leading-relaxed"
            >
              Six questions drawn from the six lessons, presented in
              a shuffled order. Pass rule: {state.pass_score} of{" "}
              {state.total} correct. Unlimited retries. This check is
              a stop-and-check gate for your own practice; it is not
              a certification or employment assessment.
            </p>

            <div className="mt-6 space-y-5">
              {state.questions.map((q, idx) => {
                const picked = picks[q.lesson_id] || null;
                const perQ = (result?.per_question || []).find(
                  (r) => r.lesson_id === q.lesson_id,
                );
                return (
                  <section
                    key={q.lesson_id}
                    data-testid={`training-knowledge-check-q-${idx}`}
                    data-lesson-id={q.lesson_id}
                    className="rounded-lg border border-border-subtle bg-bg-surface p-5"
                  >
                    <div className="flex items-center gap-2 text-fg-primary">
                      <HelpCircle className="h-4 w-4 text-accent-warm" aria-hidden="true" />
                      <h2 className="text-sm font-heading font-medium">
                        Question {idx + 1}
                      </h2>
                      <code className="ml-2 text-[10px] font-mono text-fg-muted">
                        from {q.lesson_id}
                      </code>
                    </div>
                    <p
                      data-testid={`training-knowledge-check-q-${idx}-text`}
                      className="mt-2 text-sm text-fg-primary leading-relaxed"
                    >
                      {q.question}
                    </p>
                    <div
                      role="radiogroup"
                      aria-label={`Choose one for question ${idx + 1}`}
                      className="mt-3 space-y-2"
                    >
                      {q.options.map((o) => {
                        const isPicked = picked === o.label;
                        // Round-24b: results screen no longer flags
                        // the correct option. The server does not
                        // ship `correct_label`, so the UI can only
                        // show whether the learner's own pick was
                        // right or wrong. Correct option is learned
                        // by reopening the referenced lesson.
                        const showPickedCorrect = (
                          result && isPicked && perQ?.is_correct === true
                        );
                        const showPickedWrong = (
                          result && isPicked && perQ?.is_correct === false
                        );
                        return (
                          <button
                            key={o.label}
                            type="button"
                            role="radio"
                            aria-checked={isPicked}
                            disabled={!!result}
                            data-testid={`training-knowledge-check-q-${idx}-option-${o.label}`}
                            onClick={() => pick(q.lesson_id, o.label)}
                            className={
                              "w-full text-left flex items-start gap-3 rounded-md border px-3 py-2 text-sm " +
                              (showPickedCorrect
                                ? "border-progress-teal/60 bg-progress-teal/10 text-fg-primary"
                                : showPickedWrong
                                  ? "border-destructive/60 bg-destructive/5 text-fg-primary"
                                  : isPicked
                                    ? "border-accent-warm/60 bg-accent-warm/5 text-fg-primary"
                                    : "border-border-subtle bg-bg-base text-fg-primary hover:bg-bg-surface-hover")
                            }
                          >
                            <code className="text-xs font-mono text-fg-muted mt-0.5 w-6 flex-shrink-0">
                              {o.label}.
                            </code>
                            <span className="leading-relaxed">{o.text}</span>
                            {showPickedCorrect && (
                              <CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 ml-auto" aria-hidden="true" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {result && perQ && (
                      <p
                        data-testid={`training-knowledge-check-q-${idx}-feedback`}
                        className="mt-2 text-xs text-fg-secondary leading-relaxed"
                      >
                        {perQ.is_correct === true ? (
                          <strong className="text-progress-teal">Correct.</strong>
                        ) : perQ.is_correct === false ? (
                          <>
                            <strong className="text-amber-500">Review:</strong>{" "}
                            {perQ.feedback}{" "}
                            <Link
                              to={`/training/lesson/${perQ.lesson_id}`}
                              className="underline hover:text-fg-primary"
                              data-testid={`training-knowledge-check-q-${idx}-revisit-link`}
                            >
                              Open {perQ.lesson_id}
                            </Link>
                          </>
                        ) : (
                          <>
                            <strong className="text-fg-muted">Not answered.</strong>{" "}
                            <Link
                              to={`/training/lesson/${perQ.lesson_id}`}
                              className="underline hover:text-fg-primary"
                              data-testid={`training-knowledge-check-q-${idx}-revisit-link`}
                            >
                              Open {perQ.lesson_id}
                            </Link>
                          </>
                        )}
                      </p>
                    )}
                  </section>
                );
              })}
            </div>

            {!result && (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  data-testid="training-knowledge-check-submit-btn"
                  onClick={submit}
                  disabled={!answered || submitting}
                  className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                >
                  {submitting ? "Scoring\u2026" : "Submit answers"}
                </Button>
                {!answered && (
                  <p
                    data-testid="training-knowledge-check-answer-hint"
                    className="text-xs text-fg-muted"
                  >
                    Pick an option on each of the six questions.
                  </p>
                )}
              </div>
            )}

            {result && (
              <section
                data-testid="training-knowledge-check-result"
                data-passed={result.passed ? "true" : "false"}
                className={
                  "mt-6 rounded-lg border p-5 " +
                  (result.passed
                    ? "border-progress-teal/60 bg-progress-teal/5"
                    : "border-amber-500/60 bg-amber-500/5")
                }
              >
                <h2
                  data-testid="training-knowledge-check-result-headline"
                  className="text-base font-heading font-medium text-fg-primary"
                >
                  {result.passed
                    ? "Passed — practice recorded."
                    : "Not yet. Review the flagged lessons and retry."}
                </h2>
                <p
                  data-testid="training-knowledge-check-result-score"
                  className="mt-2 text-sm text-fg-primary"
                >
                  Score: <strong>{result.score}</strong> / {result.total}.{" "}
                  Pass rule: {result.pass_score} of {result.total}.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    data-testid="training-knowledge-check-retry-btn"
                    variant="outline"
                    size="sm"
                    onClick={retry}
                    className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                    Try again (unlimited retries)
                  </Button>
                  <Button
                    data-testid="training-knowledge-check-back-training-btn"
                    size="sm"
                    onClick={() => nav("/training")}
                    className="bg-fg-primary text-bg-base hover:bg-fg-primary/90"
                  >
                    Back to Month 4
                  </Button>
                </div>
              </section>
            )}
          </article>
        )}
      </div>
    </AppShell>
  );
}
